import React, { useEffect, useMemo } from 'react';
import L from 'leaflet';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { CARTO_TILES_URL } from '../../utils/map-tiles';
import { COLORS, formatWeight, getTruckTitle } from './truck-assignments.utils';

// Centre de la France, utilisé tant qu'aucune coordonnée n'est connue
const DEFAULT_CENTER = [46.6, 2.5];

const createDeliveryIcon = ({ color, label, isUnassigned }) =>
  L.divIcon({
    html: `<div class="ta-marker ${isUnassigned ? 'ta-marker--empty' : ''}" style="${
      isUnassigned ? '' : `background-color:${color};`
    }">${label}</div>`,
    className: 'ta-marker-wrapper',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        char
      ]
  );

const createDepotIcon = (name) =>
  L.divIcon({
    html: `<div class="ta-depot"><span>▣</span><span class="ta-depot__name">${escapeHtml(
      name
    )}</span></div>`,
    className: 'ta-depot-wrapper',
    iconSize: null,
    iconAnchor: [0, 12],
  });

// Recadre la carte sur les points affichés quand la date ou le camion change
const FitBounds = ({ points, fitKey }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    map.fitBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 13 });
  }, [map, fitKey]);

  return null;
};

/**
 * Carte des affectations : marqueurs numérotés (ordre de passage, couleur du
 * camion, creux gris si non affectée), plateformes et tracés en pointillé.
 * Le filtre camion de la vue liste (bouton « Filtres ») s’applique aussi à la
 * carte : seuls les camions filtrés et les commandes à placer restent affichés.
 */
export default function AssignmentsMap({
  deliveries,
  trucks,
  factories,
  routes,
  selectedVehicleIds,
  onAssignDelivery,
  fitKey,
}) {
  const truckById = useMemo(
    () => new Map(trucks.map((truck) => [truck.id, truck])),
    [trucks]
  );

  const visibleRoutes = routes.filter((route) => {
    if (selectedVehicleIds.size === 0) return true;
    const truck = trucks.find(
      (t) => t.id === route.vehicleId || t.key === route.vehicleKey
    );
    return truck && selectedVehicleIds.has(truck.id);
  });

  const geolocated = deliveries.filter(
    (delivery) =>
      delivery.coordinates &&
      (selectedVehicleIds.size === 0 ||
        !delivery.vehicleId ||
        selectedVehicleIds.has(delivery.vehicleId))
  );
  const selectedPoints =
    selectedVehicleIds.size > 0
      ? geolocated.filter((d) => selectedVehicleIds.has(d.vehicleId))
      : [];
  const points =
    selectedPoints.length > 0
      ? selectedPoints.map((d) => [d.coordinates.lat, d.coordinates.lng])
      : [
          ...geolocated.map((d) => [d.coordinates.lat, d.coordinates.lng]),
          ...factories.map((f) => [f.lat, f.lng]),
        ];

  return (
    <div className="ta-map">
      <MapContainer
        center={
          factories.length > 0
            ? [factories[0].lat, factories[0].lng]
            : DEFAULT_CENTER
        }
        zoom={6}
        className="ta-map__leaflet"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={CARTO_TILES_URL}
        />
        <FitBounds points={points} fitKey={fitKey} />

        {visibleRoutes.map((route) => (
          <Polyline
            key={`route-${route.vehicleKey || route.vehicleId}`}
            positions={route.coordinates}
            pathOptions={{
              color: route.color,
              weight: 3,
              opacity: 0.9,
              dashArray: '6 6',
              lineJoin: 'round',
              lineCap: 'round',
            }}
          />
        ))}

        {factories.map((factory, index) => (
          <Marker
            key={factory.id || `factory-${index}`}
            position={[factory.lat, factory.lng]}
            icon={createDepotIcon(factory.name)}
          />
        ))}

        {geolocated.map((delivery) => {
          const isUnassigned = !delivery.vehicleId;
          const truck = truckById.get(delivery.vehicleId);
          return (
            <Marker
              key={delivery.id}
              position={[delivery.coordinates.lat, delivery.coordinates.lng]}
              icon={createDeliveryIcon({
                color: truck?.color || COLORS.primary,
                label: isUnassigned ? '' : delivery.order || '',
                isUnassigned,
              })}
            >
              <Popup>
                <div className="ta-popup">
                  <strong>{delivery.client}</strong>
                  <span>{delivery.address}</span>
                  <span>
                    {truck ? getTruckTitle(truck) : 'À placer'}
                    {' · '}
                    {formatWeight(delivery.weight)}
                    {delivery.etaTime && ` · ${delivery.etaTime}`}
                  </span>
                  {isUnassigned && (
                    <button
                      type="button"
                      className="ta-btn ta-btn--primary ta-popup__action"
                      onClick={() => onAssignDelivery(delivery)}
                    >
                      Affecter à un camion
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
